import React, { useReducer } from 'react';
import useTranslation from 'next-translate/useTranslation';
import { useDispatch, useSelector } from 'react-redux';

import { Button, ScreenLoader, Modal, ErrorModal } from '@/ui/index';
import {
  actions as cabinetActions,
  selectors as cabinetSelectors,
  hooks as cabinetHooks,
} from '@/domains/cabinet';
import { Payment } from '@/components/SvgComponents/Payment';
import { SuccessModal } from '@/ui/Modals/Success/SuccessModal';
import { langFormatter } from '@/utils/languageFormat';
import { isUserLoggedIn } from '@/hooks/useIsLogged';

import styles from './PopUpConfirm.module.scss';

export const PopUpConfirm = () => {
  const isLogged = isUserLoggedIn();
  const { t, lang } = useTranslation();
  const formattedLang = langFormatter({ lang });
  const actionTypes = {
    SET_SUCCESS_MODAL: 'SET_SUCCESS_MODAL',
    SET_ERROR_MODAL: 'SET_ERROR_MODAL',
    SET_INSUFFICIENT: 'SET_INSUFFICIENT',
    SET_ERROR_MESSAGE: 'SET_ERROR_MESSAGE',
  };

  const reducer = (state, action) => {
    switch (action.type) {
      case actionTypes.SET_SUCCESS_MODAL:
        return { ...state, successModal: action.payload };
      case actionTypes.SET_ERROR_MODAL:
        return { ...state, showError: action.payload };
      case actionTypes.SET_INSUFFICIENT:
        return { ...state, isInsufficient: action.payload };
      case actionTypes.SET_ERROR_MESSAGE:
        return { ...state, errorMessage: action.payload };
      default:
        return state;
    }
  };
  const initialState = {
    successModal: false,
    showError: false,
    isInsufficient: false,
    errorMessage: '',
  };
  const [state, appDispatch] = useReducer(reducer, initialState);

  const setSuccessModal = (value) =>
    appDispatch({ type: actionTypes.SET_SUCCESS_MODAL, payload: value });
  const setShowError = (value) =>
    appDispatch({ type: actionTypes.SET_ERROR_MODAL, payload: value });
  const setIsInsuccifient = (value) =>
    appDispatch({ type: actionTypes.SET_INSUFFICIENT, payload: value });
  const setErrorMessage = (message) =>
    appDispatch({ type: actionTypes.SET_ERROR_MESSAGE, payload: message });

  const { successModal, showError, isInsufficient, errorMessage } = state;

  const {
    phoneNumber,
    selectedTariff,
    accessToken,
    popUpAccount,
    isPackageOrTariff,
    popUpConfirm,
    selectedAccountId,
    popUpStep,
  } = useSelector(cabinetSelectors.selectCabinet);
  const dispatch = useDispatch();
  const eventName =
    selectedTariff?.event_type?.charAt(0).toUpperCase() +
    selectedTariff?.event_type?.slice(1);
  const [changeTariffTrigger, { isLoading: tariffLoading }] =
    cabinetHooks.useChangeTariff();
  const [changePackageTrigger, { isLoading: packageLoading }] =
    cabinetHooks.useChangePackage();
  const formatPhoneNumber = (phoneNumber) => {
    const numericPhoneNumber = phoneNumber.replace(/\D/g, '');
    const formattedPhoneNumber = `+${numericPhoneNumber.slice(
      0,
      3,
    )} ${numericPhoneNumber.slice(3, 5)} ${numericPhoneNumber.slice(
      5,
      8,
    )} ${numericPhoneNumber.slice(8, 10)} ${numericPhoneNumber.slice(10)}`;
    return formattedPhoneNumber;
  };
  const cancelHandler = () => {
    if (!popUpConfirm) {
      return;
    }
    if (popUpConfirm) {
      dispatch(cabinetActions.setPopUpConfirm(false));
      dispatch(cabinetActions.setPopUpStep(isLogged ? 0 : 1));
    }
  };
  const confirmHandler = async () => {
    dispatch(cabinetActions.setPopUpConfirm(false));
    const triggerApi = isPackageOrTariff
      ? changeTariffTrigger
      : changePackageTrigger;
    const fieldId = isPackageOrTariff ? 'tariffId' : 'packageId';

    try {
      await triggerApi({
        lang: formattedLang,
        [fieldId]: selectedTariff?.ngbss_id,
        accountId: popUpStep === 0 ? selectedAccountId : popUpAccount?.id,
        accessToken:
          popUpStep === 0 ? sessionStorage.getItem('accessToken') : accessToken,
      }).unwrap();
      setSuccessModal(true);
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: `${eventName}_Purchase_success`,
        [selectedTariff.event_type]: selectedTariff.name,
        currency: 'AZN',
        value: selectedTariff?.price,
      });
      window.dataLayer.push({
        event: 'purchase',
        ecommerce: {
          transaction_id: selectedTariff?.ngbss_id,
          value: selectedTariff?.price,
          currency: 'AZN',
          items: [
            {
              item_id: selectedTariff?.id,
              item_name: selectedTariff?.name,
              price: selectedTariff?.price,
              quantity: 1,
              item_category: 'Subscription',
            },
          ],
        },
      });
    } catch (error) {
      setErrorMessage(error.message);
      setShowError(true);
      setIsInsuccifient(error.code === 'INSUFFICIENT_FUNDS');
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: `${eventName}_Purchase_error`,
      });
    }
  };
  const successHandler = () => {
    if (successModal) {
      dispatch(cabinetActions.setTariffModal(false));
      dispatch(cabinetActions.setPopUpStep(isLogged ? 0 : 1));
      setSuccessModal(false);
    }
  };

  const errorHandler = () => {
    setShowError(false);
    dispatch(cabinetActions.setPopUpStep(isLogged ? 0 : 1));
    if (isInsufficient) {
      window?.open(
        'https://hesab.az/unregistered/#/direct-pay/mobile/bakcell/parameters',
        '_blank',
      );
    }
  };

  return (
    <>
      <ScreenLoader visible={tariffLoading || packageLoading} />
      <Modal
        className={styles.confirmModalOuter}
        active={popUpConfirm}
        setActive={() => cancelHandler()}
        itemScope
        itemType='https://schema.org/ConfirmAction'
      >
        <div className={styles.confirmModal}>
          <Payment />
          <h2>{formatPhoneNumber(phoneNumber)}</h2>
          <p>
            {t('cabinet:common.popupConfirm', {
              amount: selectedTariff?.price,
              name: selectedTariff?.name,
            })}
          </p>
          <div className={styles.confirmModal__buttons}>
            <div className={styles.confirmModal__innerDiv}>
              <Button onClick={cancelHandler} variant='transparent'>
                {t('common:Cancel')}
              </Button>
              <Button onClick={confirmHandler}>{t('common:Confirm')}</Button>
            </div>
          </div>
        </div>
      </Modal>
      <SuccessModal active={successModal} setSuccessModal={successHandler} />
      <ErrorModal
        active={showError}
        icon='/assets/error.png'
        setActive={errorHandler}
        isInsufficient={isInsufficient}
      >
        {errorMessage}
      </ErrorModal>
    </>
  );
};
